import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Center, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react';
import { VideoCard } from '@src/components/sco/Education/VideoLanding/VideoCard';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { gql, gqlArgs, gqlVars } from '@src/lib/gql';
import { useEffect } from 'react';
import { Fade } from 'react-awesome-reveal';
type VideoListProps = {
  keywords?: string;
  termSlugs?: string[];
  onResults?: (hasResults: boolean) => void;
};
export const VideoList = props => {
  const $ = _c(28);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t0;
  if ($[0] !== props) {
    t0 = VideoList.queries.videoLists(props);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.keywords || $[3] !== props.termSlugs) {
    t1 = {
      variables: {
        termSlugs: props.termSlugs,
        keywords: props.keywords
      }
    };
    $[2] = props.keywords;
    $[3] = props.termSlugs;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const query = useQuery(t0, t1);
  let t2;
  if ($[5] !== props || $[6] !== query.data?.videoLists?.length) {
    t2 = () => {
      const hasResults = query.data?.videoLists?.length > 0;
      props.onResults?.(hasResults);
    };
    $[5] = props;
    $[6] = query.data?.videoLists?.length;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = query.data?.videoLists;
  let t4;
  if ($[8] !== t3) {
    t4 = [t3];
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useEffect(t2, t4);
  if (query.error) {
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <>Error</>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  if (query.loading || !query.data) {
    let t5;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Center><Spinner /></Center>;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  if (query.data?.videoLists.length === 0) {
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Center padding={8} width="full"><Text textStyle="labelMd">No results found for this topic</Text></Center>;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  let t5;
  if ($[13] !== getBreakpointValue) {
    t5 = getBreakpointValue([4, 6, 8, 8, 8]);
    $[13] = getBreakpointValue;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== getBreakpointValue) {
    t6 = getBreakpointValue([1, 2, 2, 3, 3, 4]);
    $[15] = getBreakpointValue;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== getBreakpointValue) {
    t7 = getBreakpointValue([4, 5, 8, 8, 8]);
    $[17] = getBreakpointValue;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== query.data?.videoLists) {
    t8 = query.data?.videoLists?.map(_temp);
    $[19] = query.data?.videoLists;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <SimpleGrid columns={t6} spacingX={t7} spacingY={12} w="100%">{t8}</SimpleGrid>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t5 || $[26] !== t9) {
    t10 = <VStack align="flex-start" gap={t5} width="full">{t9}</VStack>;
    $[25] = t5;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
};
VideoList.fragments = {
  VideoList: () => gql`
    fragment _ on VideoList {
      id
      slug
      featuredImage {
        id
        url(transformation: { image: { resize: { fit: max, width: 1000 } } })
      }
      shortDescription
      longDescription
      people {
        id
        firstName
        lastName
        title
        companyName
        companyLogo {
          id
          url
        }
        avatar {
          id
          url
        }
      }
      videos {
        id
        slug
        videoPreviewUrl
        title
        shortDescription
        longDescription
        subtitle
        featuredImage {
          id
          url
        }
      }
      title
      terms {
        id
        name
        slug
      }
    }
  `
};
VideoList.queries = {
  videoLists: (props: VideoListProps) => {
    const vars = gqlVars({
      '$keywords: String': props.keywords,
      '$termSlugs: [String]': props.termSlugs?.length > 0
    });
    const where = gqlArgs({
      'where: ': {
        '_search: $keywords': props.keywords,
        'terms_some: { slug_in: $termSlugs }': props.termSlugs?.length > 0
      }
    });
    return gql`
      query ${vars} {
        videoLists(
          orderBy: publishedAt_DESC
          ${where}
        ) {
          ${VideoList.fragments.VideoList()}
        }
      }
    `;
  }
};
function _temp(video) {
  return <Fade key={video.id} cascade={true} damping={0.3} duration={1300} triggerOnce={true}><VideoCard avatar={video.people[0].avatar?.url} companyLogo={video.people[0].companyLogo?.url} firstName={video.people[0].firstName} image={video.featuredImage?.url} lastName={video.people[0].lastName} title={video.title} url={`/videos/${video.slug}/${video.videos[0]?.slug}?play=1`} video={video.videos[0]?.videoPreviewUrl} /></Fade>;
}